import {
  getDefaultContractTypeForServiceType,
  getDefaultTemplateTypeForServiceType,
} from '../utils/consistencyRules'
import {
  mapWorkTypeToProjectType,
  type ServiceCategoryOption,
} from './workTypeMapping'
import {
  parseLeadProjects,
  resolveLeadProject,
  type ProjectItem,
} from './propertyProjectSearch'

export interface ProposalRequestRow {
  id?: string
  title?: string | null
  description?: string | null
  project_type?: string | null
  service_type?: string | null
  property_id?: string | null
  lead_project_id?: string | null
  property_address?: string | null
  property_type?: string | null
  preferred_start_date?: string | null
  preferred_completion_date?: string | null
  special_requirements?: string | null
  accessibility_needs?: string | null
  notes?: unknown
  customer_id?: string | null
  lead_id?: string | null
  estimated_budget?: number | null
  urgency_level?: string | null
  request_number?: string | null
}

export interface ProposalDefaultsFromRequest {
  title: string
  description: string
  service_type: string
  template_type: string
  contract_type: '' | 'one_time' | 'recurring' | 'as_needed'
  customer_id: string
  lead_id: string
  property_id: string
  property_type: string
  start_date: string
  end_date: string
  customer_notes: string
  notes: Array<{ timestamp: string; note: string }>
  isTitleManual: boolean
  bannerHints: {
    estimated_budget?: number | null
    urgency_level?: string | null
    request_number?: string | null
  }
}

/** Map request project_type (category id or legacy slug) to proposal service_type slug. */
export function mapRequestProjectTypeToServiceType(
  projectType: string | null | undefined,
  serviceCategories: ServiceCategoryOption[] = []
): string {
  if (!projectType?.trim()) return ''

  const category = serviceCategories.find((c) => c.id === projectType)
  if (category) {
    const nameLower = category.name.toLowerCase()
    if (nameLower.includes('dsny')) return 'maintenance_dsny'
  }

  const slug = mapWorkTypeToProjectType(projectType, serviceCategories)
  if (slug) return slug

  const normalized = projectType.trim().toLowerCase()
  const legacySlugs = [
    'maintenance',
    'maintenance_prevailing_wage',
    'cleaning',
    'landscaping',
    'security',
    'custom',
    'maintenance_dsny',
    'dsny_prevailing_wage',
  ]
  if (legacySlugs.includes(normalized)) return normalized

  return ''
}

export function mergeRequestCustomerNotes(request: ProposalRequestRow): string {
  const parts: string[] = []
  if (request.special_requirements?.trim()) {
    parts.push(`Special Requirements:\n${request.special_requirements.trim()}`)
  }
  if (request.accessibility_needs?.trim()) {
    parts.push(`Access Info:\n${request.accessibility_needs.trim()}`)
  }
  return parts.join('\n\n')
}

function parseRequestNotes(raw: unknown): Array<{ timestamp: string; note: string }> {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw
      .filter((n) => n && typeof n === 'object' && 'note' in n)
      .map((n: any) => ({
        timestamp: n.timestamp || new Date().toISOString(),
        note: String(n.note || ''),
      }))
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parseRequestNotes(parsed)
    } catch {
      return []
    }
  }
  return []
}

/** Resolve property_id for proposal form from request + lead projects. */
export function resolvePropertyIdFromRequest(
  request: ProposalRequestRow,
  projects: ProjectItem[] = []
): string {
  const matched = resolveLeadProject(
    projects,
    request.lead_project_id,
    request.property_address
  )
  if (matched) {
    const index = projects.indexOf(matched)
    return matched.id || (index >= 0 ? `project-${index}` : '')
  }

  if (request.property_id) {
    const pid = String(request.property_id)
    if (pid.startsWith('project-')) return pid
    if (/^[0-9a-f-]{36}$/i.test(pid)) return pid
    const byId = projects.find((p) => p.id && String(p.id) === pid)
    if (byId) {
      const index = projects.indexOf(byId)
      return byId.id || (index >= 0 ? `project-${index}` : '')
    }
  }

  if (projects.length === 1) {
    const only = projects[0]
    const index = projects.indexOf(only)
    return only.id || (index >= 0 ? `project-${index}` : 'project-0')
  }

  return ''
}

export function buildProposalDefaultsFromRequest(
  request: ProposalRequestRow,
  serviceCategories: ServiceCategoryOption[] = [],
  projects: ProjectItem[] = []
): ProposalDefaultsFromRequest {
  const serviceType =
    request.service_type?.trim() ||
    mapRequestProjectTypeToServiceType(request.project_type, serviceCategories)

  const templateType = serviceType ? getDefaultTemplateTypeForServiceType(serviceType) : ''
  const contractTypeRaw = serviceType ? getDefaultContractTypeForServiceType(serviceType) : ''
  const contractType: '' | 'one_time' | 'recurring' =
    contractTypeRaw === 'one_time' || contractTypeRaw === 'recurring' ? contractTypeRaw : ''

  const matchedProject = resolveLeadProject(
    projects,
    request.lead_project_id,
    request.property_address
  )

  return {
    title: request.title?.trim() || '',
    description: request.description?.trim() || '',
    service_type: serviceType,
    template_type: templateType || '',
    contract_type: contractType,
    customer_id: request.customer_id || '',
    lead_id: request.lead_id || '',
    property_id: resolvePropertyIdFromRequest(request, projects),
    property_type: request.property_type?.trim() || matchedProject?.type || '',
    start_date: request.preferred_start_date || '',
    end_date: request.preferred_completion_date || '',
    customer_notes: mergeRequestCustomerNotes(request),
    notes: parseRequestNotes(request.notes),
    isTitleManual: Boolean(request.title?.trim()),
    bannerHints: {
      estimated_budget: request.estimated_budget,
      urgency_level: request.urgency_level,
      request_number: request.request_number,
    },
  }
}

/** Parse lead projects from raw DB value when building defaults. */
export function projectsFromLeadRaw(raw: unknown): ProjectItem[] {
  return parseLeadProjects(raw)
}
